import { Injectable } from '@angular/core';
import { HttpBackendService } from '../http/http.backend-service';
import { 
  ForumCategoryInterface, 
  ForumThreadInterface, 
  ForumMessageInterface,
  ForumPaginatedResponseInterface
} from '../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class ForumBackendService {
  constructor(private http: HttpBackendService) {}

  // Public Forum Endpoints
  getCategories(): Promise<ForumCategoryInterface[]> {
    return this.http.get<ForumCategoryInterface[]>('/forum/categories').toPromise();
  }

  getThreads(limit: number = 20, offset: number = 0): Promise<{
    threads: ForumThreadInterface[];
    total: number;
    pagination: { limit: number; offset: number };
  }> {
    return this.http.get<{
      threads: ForumThreadInterface[];
      total: number;
      pagination: { limit: number; offset: number };
    }>(`/forum/threads?limit=${limit}&offset=${offset}`).toPromise();
  }

  getThreadsByCategory(categoryID: number, limit: number = 20, offset: number = 0): Promise<{
    category: ForumCategoryInterface;
    threads: ForumThreadInterface[];
    total: number;
    pagination: { limit: number; offset: number };
  }> {
    return this.http.get<{
      category: ForumCategoryInterface;
      threads: ForumThreadInterface[];
      total: number;
      pagination: { limit: number; offset: number };
    }>(`/forum/threads/category/${categoryID}?limit=${limit}&offset=${offset}`).toPromise();
  }

  getThreadMessages(threadID: number, limit: number = 20, offset: number = 0): Promise<{
    thread: ForumThreadInterface;
    messages: ForumMessageInterface[];
    total: number;
    pagination: { limit: number; offset: number };
  }> {
    return this.http.get<{
      thread: ForumThreadInterface;
      messages: ForumMessageInterface[];
      total: number;
      pagination: { limit: number; offset: number };
    }>(`/forum/messages/thread/${threadID}?limit=${limit}&offset=${offset}`).toPromise();
  }

  createThread(categoryID: number, title: string, content: string): Promise<ForumThreadInterface> {
    return this.http.post<ForumThreadInterface>('/forum/threads', {
      categoryID,
      title,
      content
    }).toPromise();
  }

  createMessage(threadID: number, content: string): Promise<ForumMessageInterface> {
    return this.http.post<ForumMessageInterface>('/forum/messages', {
      threadID,
      content
    }).toPromise();
  }

  editMessage(messageID: number, content: string): Promise<ForumMessageInterface> {
    return this.http.put<ForumMessageInterface>(`/forum/messages/${messageID}`, {
      content
    }).toPromise();
  }

  deleteMessage(messageID: number): Promise<{ message: string }> {
    return this.http.delete<{ message: string }>(`/forum/messages/${messageID}`).toPromise();
  }

  // Admin Forum Endpoints
  createCategory(name: string, description: string, displayOrder?: number): Promise<ForumCategoryInterface> {
    return this.http.post<ForumCategoryInterface>('/admin/forum/categories', {
      name,
      description,
      displayOrder
    }).toPromise();
  }

  updateCategory(categoryID: number, name: string, description: string, displayOrder?: number): Promise<ForumCategoryInterface> {
    return this.http.put<ForumCategoryInterface>(`/admin/forum/categories/${categoryID}`, {
      name,
      description,
      displayOrder
    }).toPromise();
  }

  deleteCategory(categoryID: number): Promise<{ message: string }> {
    return this.http.delete<{ message: string }>(`/admin/forum/categories/${categoryID}`).toPromise();
  }

  deleteThread(threadID: number): Promise<{ message: string }> {
    return this.http.delete<{ message: string }>(`/admin/forum/threads/${threadID}`).toPromise();
  }

  setThreadReadOnly(threadID: number, isReadOnly: boolean): Promise<{ message: string; threadID: number; isReadOnly: boolean }> {
    return this.http.put<{ message: string; threadID: number; isReadOnly: boolean }>(`/admin/forum/threads/${threadID}/readonly`, {
      isReadOnly
    }).toPromise();
  }

  updateUserForumPermissions(userID: number, canPostInForum: boolean): Promise<{
    message: string;
    userID: number;
    username: string;
    canPostInForum: boolean;
  }> {
    return this.http.put<{
      message: string;
      userID: number;
      username: string;
      canPostInForum: boolean;
    }>(`/admin/forum/users/${userID}/forum-permissions`, {
      canPostInForum
    }).toPromise();
  }

  adminDeleteMessage(messageID: number): Promise<{ message: string }> {
    return this.http.delete<{ message: string }>(`/admin/forum/messages/${messageID}`).toPromise();
  }

  updateThread(threadID: number, title?: string, categoryID?: number, isReadOnly?: boolean, isPinned?: boolean): Promise<ForumThreadInterface> {
    return this.http.put<ForumThreadInterface>(`/admin/forum/threads/${threadID}`, {
      title,
      categoryID,
      isReadOnly,
      isPinned
    }).toPromise();
  }
}